const badgeFields = ['locked', 'unlocked', 'locked_thumbnail', 'unlocked_thumbnail'];
const resourceKey = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,127}$/;

/** 图片发布只使用实际资源，展示资料的占位由 catalogRows 单独投影。 */
export function resourceDraft(resources) {
  if (!Number.isInteger(resources?.catalog_version) || resources.catalog_version < 1
    || !resources.badges || !resources.reward_slots) {
    throw new Error('图片资源格式无效，请刷新后重试');
  }
  const badges = {};
  for (const [id, images] of Object.entries(resources.badges)) {
    badges[id] = Object.fromEntries(badgeFields.map(key => [key, String(images?.[key] || '')]));
  }
  const reward_slots = {...resources.reward_slots};
  const entries = Object.fromEntries(Object.entries(resources.entries || {}).map(([id, entry]) => [id, {
    name: entry.name || '', note: entry.note || '', reward_keys: [...(entry.reward_keys || [])],
  }]));
  return {catalog_version: resources.catalog_version, revision: resources.revision, badges, reward_slots, entries};
}

export function catalogRows(catalog, resources) {
  const known = new Map((catalog?.achievements || []).map(row => [row.achievement_id, row]));
  const ids = new Set([...Object.keys(resources?.badges || {}), ...known.keys()]);
  return [...ids].map(id => {
    const definition = known.get(id);
    const entry = resources.entries?.[id];
    return {
      ...(definition || {}), achievement_id: id,
      name: entry?.name || definition?.name || id,
      operator_name: entry?.name || '', operator_note: entry?.note || '',
      display_images: resources.badges[id] || {},
      levels: (definition?.levels || []).map(level => ({
        ...level,
        reward_items: (level.reward_items || []).map(reward => reward.type === 'IMAGE'
          ? {...reward, item_id: resources.reward_slots?.[reward.resource_key] || ''}
          : {...reward}),
      })),
      resource_only: !definition,
      reference_only: !Object.hasOwn(resources.badges, id),
    };
  });
}

export function rewardKeys(catalog, resources, id) {
  const definition = (catalog?.achievements || []).find(row => row.achievement_id === id);
  return [...new Set([
    ...(definition?.levels || []).flatMap(level => (level.reward_items || [])
      .filter(reward => reward.type === 'IMAGE').map(reward => reward.resource_key)),
    ...(resources.entries?.[id]?.reward_keys || []),
  ])];
}

export function addResourceEntry(resources, {id, name = '', note = '', reward_keys = []}) {
  if (!resourceKey.test(id) || ['__proto__', 'constructor', 'prototype'].includes(id)) throw new Error('请输入有效成就 ID（字母、数字、下划线、点、冒号或连字符，最长 128 字符）');
  if (Object.hasOwn(resources.badges, id)) throw new Error('成就 ID 已存在，请在原条目上配图');
  if (name.length > 200 || note.length > 2000) throw new Error('名称或备注过长');
  if (reward_keys.some(key => !resourceKey.test(key) || ['__proto__', 'constructor', 'prototype'].includes(key)
    || Object.hasOwn(resources.reward_slots, key))) throw new Error('奖励图片槽位无效或已存在');
  resources.badges[id] = Object.fromEntries(badgeFields.map(key => [key, '']));
  resources.entries[id] = {name: name.trim(), note: note.trim(), reward_keys: [...new Set(reward_keys)]};
  for (const key of reward_keys) resources.reward_slots[key] = '';
}

export function badgeReady(images) {
  return badgeFields.every(key => Boolean(images?.[key]));
}

/** 阶段名称供运营查看，底层仍使用原有绑定键，不能重新生成或修改。 */
export function rewardImageRows(catalog, resources, id) {
  const definition = (catalog?.achievements || []).find(row => row.achievement_id === id);
  const labels = new Map();
  (definition?.levels || []).forEach((level, levelIndex) => {
    const imageRewards = (level.reward_items || []).filter(reward => reward.type === 'IMAGE');
    imageRewards.forEach((reward, imageIndex) => {
      const stage = level.order || levelIndex + 1;
      const suffix = imageRewards.length > 1 ? ` ${imageIndex + 1}` : '';
      labels.set(reward.resource_key, `第 ${stage} 阶段 · 奖励图片${suffix}`);
    });
  });
  return rewardKeys(catalog, resources, id).map((resource_key, index) => ({
    resource_key,
    label: labels.get(resource_key) || `奖励图片 ${index + 1}（阶段资料未录入）`,
  }));
}
